import * as React from 'react';
import AutomationOptionsCards from 'blocks/AutomationOptions/AutomationOptionsCards';
import {
  AutoPostOptions,
  DestinationSteps,
} from 'blocks/AutomationOptions/types';
import useAutomationStepOptions from 'blocks/AutomationOptions/useAutomationStepOptions';
import DestinationPlatforms, {
  checkIfDestinationPresetExists,
  DestinationPlatform,
  DestinationPlatformsVideoTypes,
  getPlatformConfig,
  VideoTypeConfig,
} from 'blocks/DestinationPlatforms';

import { VideoTypes } from 'types';
import { DestinationOptions } from '../containers/AutomationWorkflowWizard/types';
import {
  checkViewIsAutoPosting,
  checkViewIsClipSelection,
} from '../containers/AutomationWorkflowWizard/utils';
import { destinationStepBlock as block } from '../utils';

export interface AutomationDestinationStepProps {
  destinationOptions: DestinationOptions;
  onDestinationOptionsChange?: (props: DestinationOptions) => void;
}

const AutomationDestinationStep: React.FC<AutomationDestinationStepProps> = props => {
  const { destinationOptions, onDestinationOptionsChange } = props;
  const { stepView, automationOption, platform, clipSelectionMethod } =
    destinationOptions || {};

  const {
    autoPostingOptions,
    autoPostingOptionsMetadata,
  } = useAutomationStepOptions();

  const platformConfig = getPlatformConfig(platform, 'automation');

  const handleAutoPostingOptionsChange = React.useCallback(
    (options: DestinationOptions): void => {
      onDestinationOptionsChange({
        ...options,
        autopostVideoToYouTube:
          options.automationOption === AutoPostOptions.AUTO_POST,
      });
    },
    [onDestinationOptionsChange],
  );

  const handleVideoTypeClick = (selectedVideoType: VideoTypeConfig) => {
    const {
      destination,
      videoType,
      aspectRatioName,
      clipDuration,
    } = selectedVideoType.wizardSettings;

    onDestinationOptionsChange({
      stepView: destination,
      isDestinationOptionsComplete: !destination,
      videoType,
      platformVideoType: selectedVideoType.id,
      aspectRatioName,
      clipDuration,
    });
  };

  const handlePlatformSelection = React.useCallback(
    (newPlatform: DestinationPlatform): void => {
      const shouldSelectVideoTypes = checkIfDestinationPresetExists(
        newPlatform,
      );

      onDestinationOptionsChange({
        stepView: DestinationSteps.VIDEO_TYPES,
        platform: newPlatform,
        videoType: shouldSelectVideoTypes ? null : VideoTypes.RANDOM_CLIP,
        platformVideoType: shouldSelectVideoTypes ? null : 'post',
        aspectRatioName: !shouldSelectVideoTypes ? 'square' : null,
      });
    },
    [onDestinationOptionsChange],
  );

  if (stepView === DestinationSteps.VIDEO_TYPES) {
    return (
      <div className={block('video-types-list-container')}>
        <DestinationPlatformsVideoTypes
          icon={platformConfig?.icon}
          videoTypes={platformConfig?.videoTypes}
          onClick={handleVideoTypeClick}
          onBackClick={() =>
            onDestinationOptionsChange({
              stepView: DestinationSteps.CHOOSE_DESTINATION,
            })
          }
        />
      </div>
    );
  }

  if (checkViewIsClipSelection(stepView)) {
    return (
      <AutomationOptionsCards
        metadata={autoPostingOptionsMetadata.clipSelection}
        options={autoPostingOptions.clipSelection}
        value={clipSelectionMethod}
        currentStep={stepView}
        onChange={onDestinationOptionsChange}
      />
    );
  }

  if (checkViewIsAutoPosting(stepView)) {
    return (
      <AutomationOptionsCards
        metadata={autoPostingOptionsMetadata[platform]}
        options={autoPostingOptions[platform]}
        value={automationOption}
        currentStep={stepView}
        onChange={handleAutoPostingOptionsChange}
      />
    );
  }

  return (
    <DestinationPlatforms
      contentsClassName={block('destination-options')}
      onPlatformSelection={handlePlatformSelection}
    />
  );
};

export default AutomationDestinationStep;
